/**
 * Hook to fetch and cache available payment methods
 * Used to determine if we should show coinflow-only checkout or full payment method selection
 *
 * For authenticated users, reads from the unified /user/init response (no extra API call).
 * The checkCoinflowOnlyModes utility still makes a direct API call for non-React contexts.
 */

import { useMemo } from 'react';
import { apiClient } from '@/lib/api-client';
import { useUserInitAvailableMethods } from '@/hooks/queries/useUserInitQuery';

interface AvailablePaymentMethods {
  methods: string[];
  methodProcessors: Record<string, string[]>;
  loading: boolean;
  error: string | null;
  /** True if only coinflow_hosted is available (no card, ach, google_pay, apple_pay) */
  coinflowHostedOnly: boolean;
  /** @deprecated Use coinflowHostedOnly instead */
  coinflowOnly: boolean;
  refetch: () => Promise<void>;
}

function hasPrimaryMethods(methods: string[]): boolean {
  // Check if any payment methods that require the selector are enabled
  // This includes card-based methods AND alternative methods like Cash App and Crypto
  return methods.some(m => 
    m === 'card' || 
    m === 'ach' || 
    m === 'google_pay' || 
    m === 'apple_pay' ||
    m === 'cashapp' ||
    m === 'crypto'
  );
}

export function useAvailablePaymentMethods(): AvailablePaymentMethods {
  const initData = useUserInitAvailableMethods();

  const methods = initData?.methods || [];
  const methodProcessors = initData?.methodProcessors || {};
  const loading = !initData;

  const { coinflowHostedOnly, coinflowOnly } = useMemo(() => {
    if (loading) return { coinflowHostedOnly: false, coinflowOnly: false };
    const result = !hasPrimaryMethods(methods) && methods.includes('coinflow_hosted');
    return { coinflowHostedOnly: result, coinflowOnly: result };
  }, [loading, methods]);

  return {
    methods,
    methodProcessors,
    loading,
    error: null,
    coinflowHostedOnly,
    coinflowOnly,
    refetch: async () => {}, // No-op: data refreshes via /user/init query invalidation
  };
}

/**
 * Utility function to check Coinflow-only status without using the hook
 * Useful for one-time checks outside of React components
 */
export async function checkCoinflowOnlyModes(): Promise<{
  coinflowHostedOnly: boolean;
  /** @deprecated Use coinflowHostedOnly instead */
  coinflowOnly: boolean;
}> {
  try {
    const response = await apiClient.get('/payments/available-methods');
    const availableMethods = response.methods || [];
    // Only show coinflow-only mode if no primary methods AND coinflow_hosted is the only option
    const result = !hasPrimaryMethods(availableMethods) && availableMethods.includes('coinflow_hosted');
    return { coinflowHostedOnly: result, coinflowOnly: result };
  } catch (err) {
    console.error('Error checking Coinflow-only status:', err);
    return { coinflowHostedOnly: false, coinflowOnly: false };
  }
}

/**
 * @deprecated Use checkCoinflowOnlyModes instead
 */
export async function checkCoinflowOnly(): Promise<boolean> {
  const result = await checkCoinflowOnlyModes();
  return result.coinflowOnly;
}
